import { useCallback, useState, useRef } from 'react';
import { useFocusEffect } from '@react-navigation/native';

import { OPTabInReducer } from '@components/operatorPanel/interfaces';
import { buildOPTabRequirementsContext } from '@components/operatorPanel/helpers/buildOPTabRequirementsContext';
import { NullableBatch, tabUpdaters } from '@components/operatorPanel/components/OperatorPanelInformation/OperatorPanelInformationTabs/constants';
import { DOCUMENT_ENTITY_TYPE, DOCUMENT_TYPE_TAG_KEY_MAP } from '@components/imageHandling/common';
import {
  selectCurrentBatchId,
  selectTabs,
  selectTabsLoading,
  setCurrentBatchId,
  setOperatorPanelTabs,
  setTabsLoading,
} from '../../../../../../reducers/operatorPanel';
import { scheduledProductionOperationIdParam } from '@components/operatorPanel/constants/operatorPanel.constants';
import { OperationSearchItem } from '@interfaces/search';
import { ReducerEntityWithLoadingState } from '@v2/pages/common/interfaces';
import { loadSingleBatch, selectSingleBatchData } from '@reducers/batch';
import { navigationRef } from '@helpers';
import { operationsSearch } from '@reducers/search';
import { useImageHandlingViewModelWithContextUpdate } from '@v2/components/imageHandling/hooks/useImageHandlingViewModelWithContextUpdate';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { useSelector } from 'react-redux';
import { selectSelectedItem } from '@reducers/machines';
import { selectIsWithERP } from '@reducers/membership';
import { Machine } from '@interfaces/machines';

export const useManageOPInformationTabs = () => {
  const route = navigationRef.current?.getCurrentRoute();
  const dispatch = useAppDispatch();

  const tabs: OPTabInReducer[] = useSelector(selectTabs);
  const tabsLoading: boolean = useSelector(selectTabsLoading);
  const batch: ReducerEntityWithLoadingState<NullableBatch> = useSelector(selectSingleBatchData);
  const selectedItem: Machine = useSelector(selectSelectedItem);
  const isWithERP: boolean | null = useSelector(selectIsWithERP);
  const currentBatchId: number | null = useSelector(selectCurrentBatchId);

  const [jobs, setJobs] = useState<ReducerEntityWithLoadingState<OperationSearchItem[]>>({ data: [], wasLoaded: false });
  const [shouldUpdateBatchId, setShouldUpdateBatchId] = useState<boolean>(true);
  const [batchLoading, setBatchLoading] = useState<boolean>(true);
  const [localTabs, setLocalTabs] = useState<{ apiTabs: OPTabInReducer[]; imageTabs: OPTabInReducer[] }>({
    apiTabs: tabs,
    imageTabs: tabs,
  });
  const [tabUpdateFlags, setTabUpdateFlags] = useState({ apiTabsHandled: false, imageTabsHandled: false });
  const setLoadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tabsLoaderDisabled = useRef<boolean>(false);

  const selectedJobId = route?.params?.[scheduledProductionOperationIdParam];
  const isMachinePerspective = selectedJobId == null;

  const selectedJob = selectedJobId != null ? jobs?.data?.find((j) => j?.productionOperationId === +selectedJobId) : null;

  const batchId = currentBatchId;

  const {
    state: { imagesDocuments: images, loading },
    actions: { loadImagesDocuments },
  } = useImageHandlingViewModelWithContextUpdate({
    imageContext: {
      scopeId: DOCUMENT_TYPE_TAG_KEY_MAP[isMachinePerspective ? DOCUMENT_ENTITY_TYPE.Batch : DOCUMENT_ENTITY_TYPE.ProductionOperation],
      scopeReferenceId: isMachinePerspective ? batchId : selectedJobId,
    },
    retrieveAllDocuments: true,
  });

  const jobIds = selectedJobId != null ? [selectedJobId] : [...new Set(batch?.data?.links?.map((l) => l.productionOperationId))];

  const productionOrderIds = isMachinePerspective
    ? batch.data?.links?.map((l) => l.productionOrderId) ?? []
    : selectedJob
    ? [selectedJob.productionOrderId]
    : [];

  const machineId = route?.params?.id || route?.params?.params?.id;
  const productionOrderIdsHash = productionOrderIds.join(', ');
  const jobIdsHash = jobIds.join(', ');

  // load batch data
  useFocusEffect(
    useCallback(() => {
      if (!shouldUpdateBatchId) return;

      const batchAbortController = new AbortController();

      const batchId = selectedItem?.machineStatus?.productionBatchId;
      if (batchId == null) {
        setBatchLoading(false);
        return;
      }

      (async () => {
        try {
          await dispatch(loadSingleBatch(batchId, batchAbortController.signal));
          dispatch(setCurrentBatchId(batchId));
          setShouldUpdateBatchId(false);
        } catch (e) {
          console.error('Failed to fetch batch', e);
        } finally {
          setBatchLoading(false);
        }
      })();

      return () => batchAbortController.abort();
    }, [!!selectedItem, shouldUpdateBatchId])
  );

  // load jobs data
  useFocusEffect(
    useCallback(() => {
      if (jobIds.length === 0) {
        return;
      }

      const jobsAbortController = new AbortController();

      (async () => {
        setJobs((prev) => ({ ...prev, wasLoaded: false }));
        try {
          const jobsData = await dispatch(
            operationsSearch({
              data: {
                select: [
                  'LinkedNcFiles',
                  'PartId',
                  'PartNumber',
                  'ProductionOperationId',
                  'ProductionOperationNumber',
                  'ProductionOrderId',
                  'ProductionOrderNumber',
                ],
              },
              filter: { oDataFilter: `ProductionOperationId eq ${jobIds.join(' or ProductionOperationId eq ')}`, orderBy: ['ModifiedOn desc'] },
              select: null,
              resetHistory: true,
              ignoreHistory: true,
              signal: jobsAbortController.signal,
            })
          );

          if (jobsAbortController.signal.aborted) return;

          setJobs({ data: jobsData?.searchResult ?? [], wasLoaded: true });
        } catch (error) {
          if (jobsAbortController.signal.aborted) return;
          console.error('Failed to load jobs', error);
          setJobs((prev) => ({ data: prev.data ?? [], wasLoaded: true }));
        }
      })();

      return () => {
        jobsAbortController.abort();
      };
    }, [jobIdsHash])
  );

  //load images
  useFocusEffect(
    useCallback(() => {
      const abortController = new AbortController();

      if ((isMachinePerspective && batchId == null) || (!isMachinePerspective && selectedJobId == null)) return;

      (async () => {
        loadImagesDocuments();
      })();

      if (abortController.signal.aborted) return;
      return () => abortController.abort();
    }, [isMachinePerspective, batchId])
  );

  // handle tabs with api calls
  useFocusEffect(
    useCallback(() => {
      const isMachinePerspectiveAndNoJobs = isMachinePerspective && !batchLoading && jobIds.length === 0;

      if ((!jobs.wasLoaded || batchLoading) && !isMachinePerspectiveAndNoJobs) {
        return;
      }

      if (!tabsLoading && !tabsLoaderDisabled.current) {
        dispatch(setTabsLoading(true));
      }
      setTabUpdateFlags((prev) => ({ ...prev, apiTabsHandled: false }));

      (async () => {
        const filesArray = isWithERP ? jobs.data?.flatMap((job) => (job.linkedNcFiles ?? []).map((file) => file.ncFileId)) : [];

        if (isMachinePerspective) {
          filesArray.push(batch.data?.ncFileId);
        }

        const ncFileIds = [...new Set(filesArray)];
        const ncFileIdsForTools = (isMachinePerspective ? [batch.data?.ncFileId] : ncFileIds).filter((id): id is number => id != null);

        const promises = tabs.map(async (tab) => {
          try {
            const tabUpdater = tabUpdaters[tab.value];
            const result = tabUpdater?.api && (await dispatch(tabUpdater?.api?.({ jobIds, ncFileIds, productionOrderIds, ncFileIdsForTools })));

            return result ? { value: tab.value, count: result?.totalResultCount } : null;
          } catch (error) {
            console.error(`Error calling API for tab ${tab.value}:`, error);
            return { value: tab.value, count: 0 };
          }
        });

        const results = await Promise.allSettled(promises);
        const updatedApiTabs = localTabs.apiTabs.map((t, index) => {
          if (results[index].status === 'rejected') {
            return t;
          }

          if (t.value === results[index].value?.value) {
            return { ...t, count: results[index].value?.count };
          }

          return t;
        });
        setLocalTabs((prev) => ({ ...prev, apiTabs: updatedApiTabs }));
        setTabUpdateFlags((prev) => ({ ...prev, apiTabsHandled: true }));
      })();
    }, [jobIdsHash, jobs.wasLoaded, productionOrderIdsHash, batchLoading])
  );

  const imagesHash = images.map((d) => d.contentHash).join(', ');

  // handle tabs based on images
  useFocusEffect(
    useCallback(() => {
      if (!tabsLoading && !tabsLoaderDisabled.current) {
        dispatch(setTabsLoading(true));
      }
      setTabUpdateFlags((prev) => ({ ...prev, imageTabsHandled: false }));
      const updatedImageTabs = localTabs.imageTabs.map((t) => {
        const tabUpdater = tabUpdaters[t.value];
        if (!tabUpdater?.getDataFromImages) {
          return t;
        }

        return { ...t, count: tabUpdater.getDataFromImages(images)?.length ?? 0 };
      });
      setLocalTabs((prev) => ({ ...prev, imageTabs: updatedImageTabs }));
      setTabUpdateFlags((prev) => ({ ...prev, imageTabsHandled: true }));
    }, [imagesHash])
  );

  // Merge arrays and commit to Redux store when both flags are true
  useFocusEffect(
    useCallback(() => {
      if (tabUpdateFlags.apiTabsHandled && tabUpdateFlags.imageTabsHandled) {
        const mergedTabs = localTabs.apiTabs.map((apiTab) => {
          const imageTab = localTabs.imageTabs.find((t) => t.value === apiTab.value);
          const tabUpdater = tabUpdaters[apiTab.value];

          // If this tab uses image data, prioritize image count, otherwise use API count
          if (tabUpdater?.getDataFromImages && imageTab) {
            return { ...apiTab, count: imageTab.count };
          }

          return apiTab;
        });

        dispatch(setOperatorPanelTabs(mergedTabs));

        if (!tabsLoaderDisabled.current) {
          // Clear any existing timeout
          if (setLoadingTimeoutRef.current) {
            clearTimeout(setLoadingTimeoutRef.current);
          }

          // Set new timeout to delay setTabsLoading(false) by 300ms
          setLoadingTimeoutRef.current = setTimeout(() => {
            dispatch(setTabsLoading(false));
            tabsLoaderDisabled.current = true;
          }, 300);
        }
      }
    }, [tabUpdateFlags.apiTabsHandled, tabUpdateFlags.imageTabsHandled, localTabs])
  );

  const onUpdateBatchPress = () => {
    if (!isMachinePerspective) return;
    setShouldUpdateBatchId(true);
  };

  const shouldDisplayUpdateBatchIdButton =
    currentBatchId && isMachinePerspective && selectedItem?.machineStatus?.productionBatchId !== currentBatchId;

  const requirementsContext = buildOPTabRequirementsContext({
    isWithERP,
    isMachinePerspective,
    ncFileStatus: selectedItem?.machineStatus?.ncFileStatus,
  });

  return {
    isMachinePerspective,
    selectedJob,
    selectedJobId,
    machineId,
    shouldDisplayUpdateBatchIdButton,
    onUpdateBatchPress,
    tabs,
    isWithERP,
    selectedItem,
    tabsLoading,
    requirementsContext,
  };
};
